"use client";

import { c as _c } from "react/compiler-runtime";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { useProjectStore } from "@/stores/useProjectStore";
import type { CNSDomainEvent, CNSNotification } from "@/models/cns";
import { buildEntityLifecycleDeepLink, buildProjectConversationsPath } from "@/lib/entityDeepLinks";
import { buildWorkbenchConversationPath } from "@/lib/workbenchArtifactTabUrl";
import { notificationDisplayText, shouldOmitWorkspaceSubtitleLine } from "./notificationDisplay";
import { notificationRequiresWorkspaceRefresh } from "./workspaceMembershipNotifications";

/**
 * One row in the notification panel. Uses backend {@link CNSNotification.destination}
 * when present; otherwise {@link deepLinkFor}. Marks read on click; navigates when
 * {@code resolvedDestination} is non-null.
 */
export function NotificationItem(t0) {
  const $ = _c(60);
  const {
    notification,
    onSelected
  } = t0;
  const router = useRouter();
  const markRead = useNotificationStore(_temp);
  const currentWorkspaceId = useWorkspaceStore(_temp2);
  const workspaces = useWorkspaceStore(_temp3);
  const setCurrentWorkspace = useWorkspaceStore(_temp4);
  const refreshWorkspaces = useWorkspaceStore(_temp5);
  const fetchProjects = useProjectStore(_temp6);
  const isUnread = !notification.readAt;
  let t1;
  if ($[0] !== notification) {
    t1 = resolveDestination(notification);
    $[0] = notification;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resolvedDestination = t1;
  const hasNavigation = resolvedDestination != null && resolvedDestination.length > 0;
  let t2;
  if ($[2] !== notification.workspacePublicId || $[3] !== workspaces) {
    t2 = notification.workspacePublicId && workspaces.find(w => w.id === notification.workspacePublicId)?.name;
    $[2] = notification.workspacePublicId;
    $[3] = workspaces;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const workspaceSubtitleRaw = t2;
  let t3;
  if ($[5] !== notification || $[6] !== workspaceSubtitleRaw) {
    t3 = workspaceSubtitleRaw && !shouldOmitWorkspaceSubtitleLine(notification) ? workspaceSubtitleRaw : undefined;
    $[5] = notification;
    $[6] = workspaceSubtitleRaw;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const workspaceSubtitle = t3;
  let t4;
  if ($[8] !== currentWorkspaceId || $[9] !== fetchProjects || $[10] !== markRead || $[11] !== notification.eventType || $[12] !== notification.id || $[13] !== notification.workspacePublicId || $[14] !== onSelected || $[15] !== refreshWorkspaces || $[16] !== resolvedDestination || $[17] !== router || $[18] !== setCurrentWorkspace) {
    t4 = async function onClick() {
      markRead(notification.id);
      const url = resolvedDestination;
      if (url && notificationRequiresWorkspaceRefresh(notification.eventType)) {
        try {
          await refreshWorkspaces();
        } catch {}
      }
      if (url && notification.eventType === "workspaceMemberAddedEvent" && notification.workspacePublicId && notification.workspacePublicId !== currentWorkspaceId) {
        setCurrentWorkspace(notification.workspacePublicId);
        try {
          await fetchProjects();
        } catch {}
      }
      if (url) {
        router.push(url);
      }
      onSelected?.();
    };
    $[8] = currentWorkspaceId;
    $[9] = fetchProjects;
    $[10] = markRead;
    $[11] = notification.eventType;
    $[12] = notification.id;
    $[13] = notification.workspacePublicId;
    $[14] = onSelected;
    $[15] = refreshWorkspaces;
    $[16] = resolvedDestination;
    $[17] = router;
    $[18] = setCurrentWorkspace;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const onClick = t4;
  let t5;
  if ($[20] !== notification.createdAt) {
    t5 = safeFormatDistance(notification.createdAt);
    $[20] = notification.createdAt;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const created = t5;
  let t6;
  if ($[22] !== hasNavigation || $[23] !== notification) {
    t6 = hasNavigation ? `Open notification: ${notificationDisplayText(notification)}` : `Mark notification as read: ${notificationDisplayText(notification)}`;
    $[22] = hasNavigation;
    $[23] = notification;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const ariaLabel = t6;
  const t7 = hasNavigation ? "hover:bg-accent cursor-pointer" : "hover:bg-muted/50 cursor-default";
  const t8 = isUnread ? "bg-accent/40" : undefined;
  const t9 = !hasNavigation && "text-muted-foreground";
  let t10;
  if ($[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = cn("w-full text-left px-4 py-3 transition-colors", t7, t8, t9);
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== isUnread) {
    t11 = isUnread && <span aria-hidden={true} className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />;
    $[29] = isUnread;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = !isUnread && "pl-5";
  let t13;
  if ($[31] !== t12) {
    t13 = cn("flex-1 min-w-0 space-y-1", t12);
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = isUnread ? "font-semibold" : "text-foreground/90";
  let t15;
  if ($[33] !== t14) {
    t15 = cn("text-sm leading-snug", t14);
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== notification) {
    t16 = notificationDisplayText(notification);
    $[35] = notification;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <p className={t15}>{t16}</p>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== workspaceSubtitle) {
    t18 = workspaceSubtitle ? <p className="text-[11px] text-muted-foreground">{workspaceSubtitle}</p> : null;
    $[40] = workspaceSubtitle;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== created) {
    t19 = <p className="text-[11px] text-muted-foreground">{created}</p>;
    $[42] = created;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t13 || $[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <div className={t13}>{t17}{t18}{t19}</div>;
    $[44] = t13;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== hasNavigation) {
    t21 = hasNavigation ? <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden={true} /> : null;
    $[49] = hasNavigation;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t11 || $[52] !== t20 || $[53] !== t21) {
    t22 = <div className="flex items-start gap-3">{t11}{t20}{t21}</div>;
    $[51] = t11;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== ariaLabel || $[56] !== onClick || $[57] !== t10 || $[58] !== t22) {
    t23 = <button type="button" onClick={onClick} aria-label={ariaLabel} className={t10}>{t22}</button>;
    $[55] = ariaLabel;
    $[56] = onClick;
    $[57] = t10;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  return t23;
}
function _temp6(s_4) {
  return s_4.fetchProjects;
}
function _temp5(s_3) {
  return s_3.refreshWorkspaces;
}
function _temp4(s_2) {
  return s_2.setCurrentWorkspace;
}
function _temp3(s_1) {
  return s_1.workspaces;
}
function _temp2(s_0) {
  return s_0.currentWorkspaceId;
}
function _temp(s) {
  return s.markRead;
}
function safeFormatDistance(iso: string | null | undefined): string {
  if (!iso) return "just now";
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return "";
  }
}

/**
 * Prefer persisted backend path when the API included {@code destination} (even when null).
 */
function resolveDestination(notification: CNSNotification): string | null {
  if (notification.destination !== undefined) {
    return notification.destination;
  }
  return deepLinkFor(notification);
}

/**
 * Deep-link target fallback when {@code destination} is not on the row.
 */
function deepLinkFor(notification: CNSNotification): string | null {
  const ev: CNSDomainEvent | null | undefined = notification.event;
  const eventType = notification.eventType;
  const evRecord = ev && typeof ev === "object" ? ev as unknown as Record<string, unknown> : null;
  const eventProjectId = asString(evRecord?.projectPublicId) ?? notification.projectPublicId ?? null;
  const eventExperimentId = asString(evRecord?.experimentPublicId);
  const eventLearningId = asString(evRecord?.learningPublicId);
  const eventInsightId = asString(evRecord?.insightPublicId);
  const eventEntityType = asString(evRecord?.entityType);
  const eventEntityId = asString(evRecord?.entityPublicId);
  const conversationPublicId = asString(notification.conversationPublicId) ?? asString(evRecord?.conversationPublicId);
  if (ev) {
    switch (ev.name) {
      case "experimentAssignedEvent":
      case "experimentStatusChangedEvent":
      case "experimentDueSoonEvent":
      case "experimentOverdueEvent":
        return buildEntityLifecycleDeepLink(ev.projectPublicId, "experiment", ev.experimentPublicId, conversationPublicId);
      case "shareInviteAcceptedEvent":
        if (ev.entityType === "project") {
          return `/dashboard/projects/${ev.entityPublicId}`;
        }
        if (ev.entityType === "conversation") {
          return eventProjectId ? buildWorkbenchConversationPath(eventProjectId, ev.entityPublicId, "overview") : "/dashboard";
        }
        if (ev.entityType === "workspace") {
          return `/dashboard`;
        }
        return null;
      case "workspaceMemberAddedEvent":
        return `/dashboard`;
      case "workspaceMemberRemovedEvent":
        return null;
      case "insightUpdatedEvent":
        return ev.projectPublicId ? buildEntityLifecycleDeepLink(ev.projectPublicId, "insight", ev.insightPublicId, conversationPublicId) : null;
    }
  }
  if (eventProjectId && eventExperimentId) {
    return buildEntityLifecycleDeepLink(eventProjectId, "experiment", eventExperimentId, conversationPublicId);
  }
  if (eventProjectId && eventLearningId) {
    return buildEntityLifecycleDeepLink(eventProjectId, "learning", eventLearningId, conversationPublicId);
  }
  if (eventProjectId && eventInsightId) {
    return buildEntityLifecycleDeepLink(eventProjectId, "insight", eventInsightId, conversationPublicId);
  }
  if (eventEntityType === "conversation" && eventEntityId) {
    return eventProjectId ? buildWorkbenchConversationPath(eventProjectId, eventEntityId, "overview") : "/dashboard";
  }
  if (eventEntityType === "project" && eventEntityId) {
    return `/dashboard/projects/${eventEntityId}`;
  }
  if (eventEntityType === "workspace" && eventEntityId) {
    return "/dashboard";
  }
  if ((eventType === "experimentAssignedEvent" || eventType === "experimentStatusChangedEvent" || eventType === "experimentDueSoonEvent" || eventType === "experimentOverdueEvent") && notification.projectPublicId && notification.entityPublicId) {
    return buildEntityLifecycleDeepLink(notification.projectPublicId, "experiment", notification.entityPublicId, conversationPublicId);
  }
  if (eventType === "insightUpdatedEvent" && notification.projectPublicId && notification.entityPublicId) {
    return buildEntityLifecycleDeepLink(notification.projectPublicId, "insight", notification.entityPublicId, conversationPublicId);
  }
  if (eventType === "experimentAssignedEvent" || eventType === "experimentStatusChangedEvent" || eventType === "experimentDueSoonEvent" || eventType === "experimentOverdueEvent") {
    return notification.projectPublicId ? buildProjectConversationsPath(notification.projectPublicId) : "/dashboard";
  }
  if (eventType === "insightUpdatedEvent") {
    return notification.projectPublicId ? buildProjectConversationsPath(notification.projectPublicId) : "/dashboard";
  }
  if (eventType === "shareInviteAcceptedEvent") {
    if (notification.entityType === "conversation") {
      return "/dashboard";
    }
    if (notification.entityType === "project" && notification.entityPublicId) {
      return `/dashboard/projects/${notification.entityPublicId}`;
    }
    return "/dashboard";
  }
  if (eventType === "workspaceMemberAddedEvent") {
    return "/dashboard";
  }
  if (eventType === "workspaceMemberRemovedEvent") {
    return null;
  }
  if (eventType === "workspaceSubscriptionActivatedEvent" || eventType === "workspaceSubscriptionPlanChangedEvent" || eventType === "workspaceSubscriptionCanceledEvent" || eventType === "workspaceSubscriptionPaymentFailedEvent" || eventType === "workspaceTopupPurchasedEvent") {
    return "/dashboard/billing";
  }
  if (notification.entityType === "conversation" && notification.entityPublicId) {
    return notification.projectPublicId ? buildWorkbenchConversationPath(notification.projectPublicId, notification.entityPublicId, "overview") : "/dashboard";
  }
  if (notification.entityType === "experiment" && notification.entityPublicId && notification.projectPublicId) {
    return buildEntityLifecycleDeepLink(notification.projectPublicId, "experiment", notification.entityPublicId, conversationPublicId);
  }
  if (notification.entityType === "learning" && notification.entityPublicId && notification.projectPublicId) {
    return buildEntityLifecycleDeepLink(notification.projectPublicId, "learning", notification.entityPublicId, conversationPublicId);
  }
  if (notification.entityType === "insight" && notification.entityPublicId && notification.projectPublicId) {
    return buildEntityLifecycleDeepLink(notification.projectPublicId, "insight", notification.entityPublicId, conversationPublicId);
  }
  if (notification.entityType === "project" && notification.entityPublicId) {
    return `/dashboard/projects/${notification.entityPublicId}`;
  }
  if (notification.entityType === "workspace" && notification.entityPublicId) {
    return "/dashboard";
  }
  return null;
}
function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}